import { useEffect, useState } from 'react';
import { BrowserRouter, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { getActiveProfileId, getLastVisit, setLastVisit, getProfileById, getRoomProfileId as getRoomProfileIdMaybe } from './utils/profiles';
import { CharacterSheet } from './types/CharacterSheet';
import { unlockAudio } from './utils/audio';
import { Logo } from './components/Logo';
import AppRoutes from './app/AppRoutes';
import { PLAYER_KEY, ROOM_KEY, INACTIVITY_MS } from './consts/consts';
import { useAuthLifecycle } from './hooks/useAuthLifecycle';
import { useRoomConnection } from './hooks/useRoomConnection';
import { decodeRoomId, encodeRoomId } from './utils/rooms';
import { listenRoomMeta, publishCharacterSheet } from './utils/roomClient';
import { getStoredGmName } from './utils/gmFlow';
import { DEFAULT_ROOM_THEME, isRoomTheme, RoomTheme } from './types/RoomTheme';
import './app/styles/app.css';

const ROOM_THEME_CACHE_PREFIX = 'room_theme:';

function decodeRouteRoomId(pathname: string): string | null {
    const roomMatch = matchPath('/sala/:roomId', pathname);
    return roomMatch?.params.roomId ? decodeRoomId(roomMatch.params.roomId) : null;
}


function getRoomThemeCacheKey(roomId: string): string {
    return `${ROOM_THEME_CACHE_PREFIX}${roomId}`;
}

function getCachedRoomTheme(roomId: string): RoomTheme | null {
    const cachedTheme = localStorage.getItem(getRoomThemeCacheKey(roomId));
    return isRoomTheme(cachedTheme) ? cachedTheme : null;
}

const AppInner = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const initialRouteRoomId = decodeRouteRoomId(location.pathname);
    const initialCachedTheme = initialRouteRoomId ? getCachedRoomTheme(initialRouteRoomId) : null;
    const [showProfileManager, setShowProfileManager] = useState(false);
    const [activeProfileId, setActiveProfileIdState] = useState<string | null>(null);
    const [formData, setFormData] = useState<CharacterSheet>({});

    // Use Firebase Auth UID as playerId, fallback to localStorage for local dev
    const [playerId, setPlayerId] = useState<string>(() => {
        const existing = localStorage.getItem(PLAYER_KEY);
        if (existing) return existing;
        const id = Math.random().toString(36).slice(2);
        localStorage.setItem(PLAYER_KEY, id);
        return id;
    });

    const [roomId, setRoomId] = useState<string | null>(() => initialRouteRoomId);
    const [players, setPlayers] = useState<Record<string, any>>({});
    const [roomTheme, setRoomTheme] = useState<RoomTheme>(() => initialCachedTheme ?? DEFAULT_ROOM_THEME);
    const [roomThemeReady, setRoomThemeReady] = useState<boolean>(() => !initialRouteRoomId || Boolean(initialCachedTheme));
    const { authLoading, authError } = useAuthLifecycle(setPlayerId);
    const playerName = formData.name?.trim() || (roomId ? getStoredGmName(roomId, playerId) : null) || 'Anónimo';

    useEffect(() => {
        const lastVisit = getLastVisit();
        const now = Date.now();
        const activeId = getActiveProfileId();
        if (!activeId || now - lastVisit > INACTIVITY_MS) {
            setShowProfileManager(true);
            setRoomId(null); // also leave room on inactivity  
        } else {
            setActiveProfileIdState(activeId);
            const profile = getProfileById(activeId);
            setFormData(profile?.data ?? {});
        }
        setLastVisit(now);
    }, []);

    // persist roomId changes
    useEffect(() => {
        if (roomId) localStorage.setItem(ROOM_KEY, roomId);
        else localStorage.removeItem(ROOM_KEY);
    }, [roomId]);

    useEffect(() => {
        const roomMatch = matchPath('/sala/:roomId', location.pathname);
        const routeRoomId = roomMatch?.params.roomId ? decodeRoomId(roomMatch.params.roomId) : null;

        if (routeRoomId && routeRoomId !== roomId) {
            setRoomId(routeRoomId);
            return;
        }

        if (location.pathname === '/sala') {
            const fallbackRoomId = roomId ?? localStorage.getItem(ROOM_KEY);
            if (fallbackRoomId) {
                navigate(`/sala/${encodeRoomId(fallbackRoomId)}`, { replace: true });
            } else {
                navigate('/', { replace: true });
            }
        }
    }, [location.pathname, navigate, roomId]);

    useEffect(() => {
        if (!roomId) return;
        if (typeof getRoomProfileIdMaybe !== 'function') return;

        const rememberedProfileId = getRoomProfileIdMaybe(roomId);
        if (!rememberedProfileId) {
            setActiveProfileIdState(null);
            setFormData({});
            setShowProfileManager(true);
            return;
        }

        const profile = getProfileById(rememberedProfileId);
        if (!profile) {
            setActiveProfileIdState(null);
            setFormData({});
            setShowProfileManager(true);
            return;
        }

        setActiveProfileIdState(rememberedProfileId);
        setFormData(profile.data ?? {});
        setShowProfileManager(false);
    }, [roomId]);

    useEffect(() => {
        if (!roomId) {
            setRoomTheme(DEFAULT_ROOM_THEME);
            setRoomThemeReady(true);
            return;
        }
        const cachedTheme = getCachedRoomTheme(roomId);
        if (cachedTheme) {
            setRoomTheme(cachedTheme);
            setRoomThemeReady(true);
        } else {
            setRoomTheme(DEFAULT_ROOM_THEME);
            setRoomThemeReady(false);
        }

        const unsub = listenRoomMeta(roomId, meta => {
            const nextTheme = meta?.theme ?? DEFAULT_ROOM_THEME;
            setRoomTheme(nextTheme);
            setRoomThemeReady(true);
            localStorage.setItem(getRoomThemeCacheKey(roomId), nextTheme);
        });
        return () => {
            try { unsub(); } catch { /* ignore */ }
        };
    }, [roomId]);

    useRoomConnection({
        roomId,
        playerId,
        playerName,
        authLoading,
        setPlayers,
    });

    useEffect(() => {
        if (!roomId || authLoading) return;
        let cancelled = false;
        const timer = setTimeout(() => {
            if (cancelled) return;
            const sheetPayload = activeProfileId ? formData : null;
            void publishCharacterSheet(roomId, playerId, sheetPayload).catch(err => {
                console.error('publishCharacterSheet failed', err);
            });
        }, 150);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [roomId, authLoading, playerId, activeProfileId, formData]);


    useEffect(() => {
        const once = () => unlockAudio();
        window.addEventListener('click', once, { once: true });
        window.addEventListener('keydown', once, { once: true });
        return () => {
        window.removeEventListener('click', once);
        window.removeEventListener('keydown', once);
        };
    }, []);

    // Show loading state while authenticating
    if (authLoading) {
        return (
            <div className="App">
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    fontSize: '18px'
                }}>
                    Iniciando sesión...
                </div>
            </div>
        );
    }

    // Show error state if authentication failed
    if (authError) {
        return (
            <div className="App">
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    fontSize: '18px',
                    color: 'red'
                }}>
                    Error de autenticación: {authError}
                </div>
            </div>
        );
    }

    const isDisambiguationRoute = location.pathname === '/';
    const isRoomRoute = Boolean(matchPath('/sala/:roomId', location.pathname));
    const handleRoomThemeChange = (theme: RoomTheme) => {
        setRoomTheme(theme);
        setRoomThemeReady(true);
        if (roomId) {
            localStorage.setItem(getRoomThemeCacheKey(roomId), theme);
        }
    };

    if (isRoomRoute && !roomThemeReady) {
        return (
            <div className="App app-room theme-loading">
                <main>
                    <div className="room-theme-loading">Cargando…</div>
                </main>
            </div>
        );
    }

    return (
        <div className={`App ${isDisambiguationRoute ? 'app-disambiguation' : ''} ${isRoomRoute ? `app-room theme-${roomTheme}` : ''}`.trim()}>
            {!isRoomRoute && !isDisambiguationRoute && <Logo />}

            <main>
                <AppRoutes
                    formData={formData}
                    playerId={playerId}
                    roomId={roomId}
                    players={players}
                    setRoomId={setRoomId}
                    setPlayers={setPlayers}
                    setActiveProfileIdState={setActiveProfileIdState}
                    setFormData={setFormData}
                    setShowProfileManager={setShowProfileManager}
                    showProfileManager={showProfileManager}
                    activeProfileId={activeProfileId}
                    roomTheme={roomTheme}
                    onRoomThemeChange={handleRoomThemeChange}
                />
            </main>
        </div>
    );
}



const App = () => {
    return (
        <BrowserRouter>
            <AppInner />
        </BrowserRouter>
    );
};

export default App;
